import { useCallback } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { EligibilityCheckResult } from '../data-types.ts';
import { getJiraSmartLinks } from '../services/links.ts';
import { isUserOptOut } from '../services/opt-out.ts';
import { resolveInviteOnLinkInsert } from '../services/resolve-invite-on-link-insert.ts';

import { useInvitePromptFlag } from './useInvitePromptFlag.tsx';

export const useInviteOnLinkInsertEligibilityCheck = (
	flags?: FlagsStateContainer,
): ((
	pageId: string,
	content: ADFEntity,
	pageVersion: number,
	startingLinks?: Set<string>,
) => Promise<EligibilityCheckResult | undefined>) => {
	const { store } = useSmartLinkContext();
	const { cloudId: confluenceCloudId, userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async (
			pageId: string,
			content: ADFEntity,
			pageVersion: number,
			startingLinks?: Set<string>,
		): Promise<EligibilityCheckResult | undefined> => {
			if (startingLinks === undefined || flags === undefined) {
				return;
			}

			try {
				const newLinkData = getJiraSmartLinks(content, store);
				for (const [link, data] of Object.entries(newLinkData)) {
					if (!startingLinks?.has(link)) {
						const { tenantId: jiraCloudId, name } = data;
						const invitees = await resolveInviteOnLinkInsert({
							userId,
							confluenceCloudId,
							pageId,
							jiraCloudId,
						});

						if (invitees.length > 0) {
							if (isUserOptOut()) return;
							return { invitees, targetTitle: name, continueUrl: link, jiraCloudId, pageVersion };
						}
						break;
					}
				}
			} catch (error) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'checkEligibility',
						source: 'inviteOnLinkInsert',
						attributes: {
							message: String(error),
							details: { ...error },
						},
					},
				}).fire();
			}
		},
		[confluenceCloudId, createAnalyticsEvent, flags, store, userId],
	);
};

export const useShowInviteOnLinkInsertFlag = (
	flags?: FlagsStateContainer,
): (({
	invitees,
	targetTitle,
	continueUrl,
	jiraCloudId,
	pageVersion,
}: EligibilityCheckResult) => Promise<void>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showInviteFlag = useInvitePromptFlag(flags);
	return useCallback(
		async ({
			invitees,
			targetTitle,
			continueUrl,
			jiraCloudId,
			pageVersion,
		}: EligibilityCheckResult) => {
			try {
				showInviteFlag(invitees, targetTitle, continueUrl, jiraCloudId, pageVersion);
			} catch (error) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'showInviteFlag',
						source: 'inviteOnLinkInsert',
						attributes: {
							message: String(error),
							details: { ...error },
						},
					},
				}).fire();
			}
		},
		[createAnalyticsEvent, showInviteFlag],
	);
};
